import { createConsumer } from "@rails/actioncable"
import { useCallback, useEffect, useRef, useState } from "react"

import { postForm } from "@/api/client"
import { altTextSuggestionsPath } from "@/routes"

interface Subscription {
  unsubscribe: () => void
}

interface AltTextRequest {
  key: string
  file: File
  body?: string
  shouldApply: () => boolean
  onResult: (altText: string) => void
}

export function useAltTextGeneration() {
  const [generatingKeys, setGeneratingKeys] = useState<Set<string>>(new Set())
  const subscriptionsRef = useRef<Map<string, Subscription>>(new Map())
  const consumerRef = useRef<ReturnType<typeof createConsumer> | null>(null)

  function getConsumer() {
    consumerRef.current ??= createConsumer()
    return consumerRef.current
  }

  const stopGenerating = useCallback((key: string) => {
    setGeneratingKeys((prev) => {
      const next = new Set(prev)
      next.delete(key)
      return next
    })
    subscriptionsRef.current.get(key)?.unsubscribe()
    subscriptionsRef.current.delete(key)
  }, [])

  useEffect(() => {
    const subs = subscriptionsRef.current
    const consumer = consumerRef.current
    return () => {
      for (const sub of subs.values()) {
        sub.unsubscribe()
      }
      subs.clear()
      consumer?.disconnect()
    }
  }, [])

  const requestAltText = useCallback(
    ({ key, file, body, shouldApply, onResult }: AltTextRequest) => {
      if (!file.type.startsWith("image/")) return

      const requestId = crypto.randomUUID()

      // A newer request for the same media file supersedes any in-flight one.
      subscriptionsRef.current.get(key)?.unsubscribe()
      setGeneratingKeys((prev) => new Set(prev).add(key))

      const consumer = getConsumer()
      const subscription = consumer.subscriptions.create(
        { channel: "AltTextSuggestionsChannel", request_id: requestId },
        {
          received(data: { alt_text: string }) {
            if (shouldApply()) {
              onResult(data.alt_text)
            }
            stopGenerating(key)
          },
        },
      )

      subscriptionsRef.current.set(key, subscription)

      const formData = new FormData()
      formData.append("image", file)
      formData.append("request_id", requestId)
      if (body) formData.append("take_body", body)

      void postForm(altTextSuggestionsPath(), formData).then((result) => {
        if (result.ok) return
        stopGenerating(key)
      })
    },
    [stopGenerating],
  )

  return { requestAltText, generatingKeys }
}
